import type { ChildItem, HeaderMenu } from 'types/settings';
import { useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { getActivePage, useActivePage } from 'hooks/useActivePage';
import { useEventsTracker } from 'contexts/eventsTracker';

const SubMenuMobile: React.FC<{
  childItems: ChildItem[];
  activeSubmenu?: boolean;
}> = ({ childItems, activeSubmenu }) => {
  const { trackEvent } = useEventsTracker();
  const [activeSubSubMenu, setActiveSubSubMenu] = useState(false);
  return (
    <ul
      className={[
        activeSubmenu
          ? 'grid xl:hidden gap-y-6 pl-3 mt-3 border-y border-y-[#DEE0DE] py-3'
          : 'hidden',
      ].join(' ')}
    >
      {childItems?.map(({ title, url, childItems }, idx) => {
        const hasSubmenu = childItems?.length > 0;
        return (
          <li
            key={`${idx}-submenu-item-${title}`}
            className="underline-offset-small cursor-pointer transition-all duration-200 focus:outline-none outline-none no-underline"
          >
            <a
              className="flex items-center gap-x-2 leading-6 font-normal text-lg"
              href={url}
              onClick={() => {
                if (url !== '#') {
                  trackEvent({
                    event: 'select_content',
                    content_type: title,
                    item_id: 'page',
                    origin: 'main_menu',
                  });
                }
              }}
            >
              {title}
              {hasSubmenu && (
                <Image
                  src="/chevron.svg"
                  width={24}
                  height={24}
                  className=""
                  alt=""
                  onClick={e => {
                    e.preventDefault();
                    setActiveSubSubMenu(!activeSubSubMenu);
                  }}
                />
              )}
            </a>
            {hasSubmenu && (
              <SubMenuMobile
                childItems={childItems}
                activeSubmenu={activeSubSubMenu}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};

const SubMenuDesktop: React.FC<{
  childItems: ChildItem[];
  isFirstLevel?: boolean;
}> = ({ childItems, isFirstLevel = true }) => {
  const { trackEvent } = useEventsTracker();
  return (
    <ul
      className={[
        'hidden leafwell-sub-menu absolute gap-y-6 border border-[#DEE0DE] rounded-md bg-white text-base p-6 w-72 shadow-md',
        isFirstLevel ? 'top-full' : 'leafwell-sub-sub-menu left-[100%] -top-2',
      ].join(' ')}
    >
      {childItems?.map(({ title, url, childItems }, idx) => {
        const hasSubmenu = childItems?.length > 0;
        return (
          <li
            key={`${idx}-submenu-item-${title}`}
            className={[
              'underline-offset-small cursor-pointer transition-all duration-200 focus:outline-none outline-none no-underline hover:bg-lilac-50 xl:p-3 rounded-md',
              hasSubmenu ? 'relative leafwell-sub-menu-parent' : 'w-max',
            ].join(' ')}
          >
            <a
              className="flex items-center justify-between gap-x-2 leading-6"
              href={url !== '#' ? url : 'javascript:void(0)'}
              onClick={() => {
                if (url !== '#') {
                  trackEvent({
                    event: 'select_content',
                    content_type: title,
                    item_id: 'page',
                    origin: 'main_menu',
                  });
                }
              }}
            >
              {title}
              {hasSubmenu ? (
                <Image
                  className="w-5"
                  src="/arrow.svg"
                  width={20}
                  height={16}
                  alt=""
                />
              ) : null}
            </a>
            {hasSubmenu && (
              <SubMenuDesktop childItems={childItems} isFirstLevel={false} />
            )}
          </li>
        );
      })}
    </ul>
  );
};

const MainMenu: React.FC<{ headerMenu: HeaderMenu; menuVisible?: boolean }> = ({
  headerMenu: items,
}) => {
  const { asPath } = useRouter();
  const { trackEvent } = useEventsTracker();

  return (
    <nav className="xl:mx-auto">
      {items?.length > 0 ? (
        <ul className="flex flex-col md:flex-row md:gap-x-2 2xl:gap-x-10 gap-y-8">
          {items?.map(({ title, url, childItems }, idx) => {
            const hasSubmenu = childItems?.length > 0;
            const hasActiveItem =
              hasSubmenu &&
              childItems?.some(({ url }) => getActivePage(url, asPath));
            const isCurrentPage = useActivePage(url);
            const isActive = isCurrentPage || hasActiveItem;

            const [activeSubMenu, setActiveSubMenu] = useState(false);
            return (
              <li
                key={`${idx}-menu-item-${title}`}
                className={[
                  'leafwell-menu-parent relative',
                  'underline-offset-small cursor-pointer transition-all duration-200 focus:outline-none outline-none no-underline',
                  'text-xl xl:text-lg font-semibold xl:hover:bg-lilac-50 xl:p-3 xl:px-2 rounded-md',
                ].join(' ')}
              >
                <a
                  className="leafwell-menu-link relative flex items-center gap-x-2 leading-6"
                  href={url !== '#' ? url : 'javascript:void(0)'}
                  target="_self"
                  onClick={() => {
                    if (url !== '#') {
                      trackEvent({
                        event: 'select_content',
                        content_type: title,
                        item_id: 'page',
                        origin: 'main_menu',
                      });
                    }
                  }}
                >
                  {title}
                  {isActive && (
                    <Image
                      src="/menu-line.svg"
                      alt=""
                      className="absolute hidden xl:block xl:left-2/4 top-full transform -translate-x-1/2 pt-1"
                      width={89}
                      height={7}
                    />
                  )}
                  {hasSubmenu && (
                    <Image
                      src="/chevron.svg"
                      width={24}
                      height={24}
                      className=""
                      alt=""
                      onClick={e => {
                        e.preventDefault();
                        setActiveSubMenu(!activeSubMenu);
                      }}
                    />
                  )}
                </a>
                {hasSubmenu && (
                  <>
                    <SubMenuDesktop childItems={childItems} />
                    <SubMenuMobile
                      childItems={childItems}
                      activeSubmenu={activeSubMenu}
                    />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </nav>
  );
};

export default MainMenu;
